import React from 'react';
import ReactPlaceholder from 'react-placeholder/lib';
import { Field, FormErrors, reduxForm } from 'redux-form';
import classNames from 'clsx';
import Scrollbars from 'react-custom-scrollbars-2';
import { Checkbox, FormControlLabel, InputLabel } from '@material-ui/core';
import { useSelector } from 'react-redux';

import { FormsEnum, InstallmentsEnum } from 'app/shared/enums';
import {
  required,
  idCardNumber,
  minDateToday,
  maxDateToday,
  minDateForIdCard,
} from 'app/validators';
import { ScrollService } from 'app/services/scroll.service';
import {
  CheckboxControl,
  ListControl,
  DateControl,
  IdCardNumberControl,
} from 'app/components/Controls';
import { NewButton } from 'app/components/NewButton';
import { CompactHeadingSection } from 'app/components/CompactHeadingSection';
import {
  ApplicationDataFormOwnProps,
  ApplicationDataFormProps,
} from './ApplicationDataForm.interfaces';
import { useStyles } from './ApplicationDataForm.styles';
import { useApplicationDataForm } from './useApplicationDataForm';
import { useConsentHiPro } from './useConsentHiPro';
import { ConsentField } from '../ConsentField';
import { GroupConsent } from '../GroupConsent';
import { selectIsFormMicroCenterActive } from 'app/store/selectors/selectIsFormMicroCenterActive';

const noConsentErrors = ['idCard', 'idCardExpiryDate', 'idCardIssueDate'];

const ApplicationDataForm = (props: ApplicationDataFormProps) => {
  const classes = useStyles();

  const {
    insurantData,
    countries,
    idCardIndefinitelyValid,
    onFormSubmit,
    handleIdCardIndefinitelyValid,
    isLoading,
    handleIdCardIssueDateChange,
    getStartDate,
  } = useApplicationDataForm(props);

  const {
    mainConsent,
    groupedConsents,
    handleTickRequiredConsentsChange,
    allRequiredConsentsSelected,
    handleChangeGroupConsents,
    handleUnCheckedGroup,
    handleMainConsentChange,
  } = useConsentHiPro(props);

  const isFormMicroCenterActive = useSelector(selectIsFormMicroCenterActive);

  return (
    <div className={classes.root}>
      <div className={classNames(classes.section, classes.insurantData)}>
        <div>
          <ReactPlaceholder
            type="text"
            showLoadingAnimation
            rows={3}
            ready={!!insurantData}
          >
            <p
              className={classes.bold}
            >{`${insurantData?.name} ${insurantData?.lastName}`}</p>
            <p>{`${InstallmentsEnum.StreetLabel}${insurantData?.streetName} ${
              insurantData?.streetNumber
            }${
              insurantData?.apartmentNumber
                ? InstallmentsEnum.Slash + insurantData?.apartmentNumber
                : ''
            }`}</p>
            <p>{`${insurantData?.zipCode} ${insurantData?.city}`}</p>
          </ReactPlaceholder>
        </div>
        <div>
          <ReactPlaceholder
            type="text"
            showLoadingAnimation
            rows={3}
            ready={!!insurantData}
          >
            <p className={classes.epmhasized}>{insurantData?.emailAddress}</p>
            <p>{`${InstallmentsEnum.PhoneNumberLabel}${insurantData?.phoneNumber}`}</p>
            <p>{`${InstallmentsEnum.PeselLabel}${insurantData?.peselNumber}`}</p>
          </ReactPlaceholder>
        </div>
      </div>

      <form onSubmit={onFormSubmit} data-cy="mubi-installments-data-form">
        <div className={classes.section}>
          <Field
            component={IdCardNumberControl}
            name="idCard"
            label={InstallmentsEnum.IdCardNumber}
            validate={[required, idCardNumber]}
            withMarginIfValid
          />
          <Field
            component={DateControl}
            name="idCardIssueDate"
            label={InstallmentsEnum.IdCardIssueDate}
            validate={[required, maxDateToday, minDateForIdCard]}
            onChange={handleIdCardIssueDateChange}
            minDate={new Date(2001, 0, 1)}
            maxDate={new Date()}
            startDate={getStartDate('idCardIssueDate')}
            withMarginIfValid
          />
          <Field
            component={DateControl}
            name="idCardExpiryDate"
            label={InstallmentsEnum.IdCardExpiryDate}
            validate={idCardIndefinitelyValid ? [] : [required, minDateToday]}
            minDate={new Date()}
            maxDate={null}
            startDate={getStartDate('idCardExpiryDate')}
            disabled={idCardIndefinitelyValid}
          />
          <Field
            component={CheckboxControl}
            name="indefiniteIdCard"
            label={InstallmentsEnum.IdCardIndefinitelyValid}
            onChange={handleIdCardIndefinitelyValid}
            className={classes.cardIndefinitely}
          />
          <InputLabel htmlFor={'birthCountry'} className={classes.inputLabel}>
            {InstallmentsEnum.CountryOfBirth}
          </InputLabel>
          {countries && (
            <Field
              component={ListControl}
              name="birthCountry"
              validate={[required]}
              dictionary={countries}
              className={classes.countrySelect}
            />
          )}
        </div>
        <CompactHeadingSection
          title={InstallmentsEnum.LenderConsents}
          className={classes.lenderConsentsHeader}
        />
        <div className={classNames(classes.section, classes.lenderConsents)}>
          {groupedConsents && groupedConsents.length > 0 && (
            <>
              <FormControlLabel
                tabIndex={0}
                checked={allRequiredConsentsSelected}
                control={
                  <Checkbox
                    tabIndex={-1}
                    name={'tickRequiredConsents'}
                    onChange={handleTickRequiredConsentsChange}
                    color="primary"
                    data-cy="tick-required-consents"
                  />
                }
                label={InstallmentsEnum.TickRequiredConsents}
                className={classNames(
                  classes.tickRequiredConsents,
                  'focusVisibleOutline'
                )}
                onKeyDown={(e) => {
                  if (
                    e.target === e.currentTarget &&
                    (e.key === 'Enter' || e.key === ' ')
                  ) {
                    handleTickRequiredConsentsChange(
                      e,
                      !allRequiredConsentsSelected
                    );
                  }
                }}
              />
              <div className={classes.consentsSection}>
                <Scrollbars
                  autoHeight
                  autoHeightMin={0}
                  autoHeightMax={'182px'}
                  id="mubi-installments-consents-scrollbars"
                >
                  <div className={classes.consentsContainer}>
                    {mainConsent && (
                      <ConsentField
                        consent={mainConsent}
                        showAllLabel
                        onChange={handleMainConsentChange}
                      />
                    )}
                    {groupedConsents &&
                      groupedConsents.map((group) => (
                        <GroupConsent
                          key={group.code}
                          group={group}
                          handleChangeGroupConsents={handleChangeGroupConsents}
                          handleUnCheckedGroup={handleUnCheckedGroup}
                        />
                      ))}
                  </div>
                </Scrollbars>
              </div>
            </>
          )}
          <div className={classes.requiredFieldsInfo}>
            <span className={classes.requiredFielAsterisk}>
              {InstallmentsEnum.Asterisk}
            </span>
            {InstallmentsEnum.RequiredFields}
          </div>
        </div>
        <NewButton
          type="submit"
          title={InstallmentsEnum.Next}
          variant={'contained'}
          color={'primary'}
          fullWidth
          className={classes.submitButton}
          isLoading={isLoading}
          data-cy="submit-application-form-mubi-installments"
          rounded={isFormMicroCenterActive}
        />
      </form>
    </div>
  );
};

export default reduxForm<{}, ApplicationDataFormOwnProps>({
  form: FormsEnum.MubiInstallmentsDataForm,
  onSubmitFail: (errors: FormErrors<FormData>) => {
    const errorsIncludesNoConsentErrors = Object.keys(errors).some((key) =>
      noConsentErrors.includes(key)
    );

    const elementToScroll = errorsIncludesNoConsentErrors
      ? '#mubi-installments-scrollbars'
      : '#mubi-installments-consents-scrollbars';

    return ScrollService.scrollOnSubmitFail(errors, {
      scroller: document.querySelector(elementToScroll)?.firstElementChild,
    });
  },
})(ApplicationDataForm);
